import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '@/context/ThemeContext';
import { API_URL, STATUS_LABELS, formatDate } from '@/lib/utils';
import { 
  Search, Moon, Sun, Wrench, CheckCircle2, Clock, AlertCircle, 
  Package, Laptop, ChevronRight, ArrowLeft 
} from 'lucide-react';

const STATUS_ICONS = {
  received: Package,
  diagnosis: Search,
  waiting_parts: Clock,
  maintenance: Wrench,
  software_test: Laptop,
  ready: CheckCircle2,
  delivered: CheckCircle2,
  warranty: AlertCircle
};

export default function TrackingPage() {
  const { orderId: paramOrderId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [trackingId, setTrackingId] = useState(paramOrderId || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (paramOrderId) {
      fetchOrder(paramOrderId);
    }
  }, [paramOrderId]);

  const fetchOrder = async (id) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/track/${id}`);
      setOrder(response.data);
    } catch (err) {
      setError('No se encontró la orden. Verifica el código e intenta de nuevo.');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (trackingId.trim()) {
      navigate(`/track/${trackingId.trim().toUpperCase()}`);
    }
  };

  const getStatusIndex = (status) => {
    const statuses = ['received', 'diagnosis', 'waiting_parts', 'maintenance', 'software_test', 'ready', 'delivered'];
    return statuses.indexOf(status);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Wrench className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-mono font-bold text-xl tracking-tight">TechFix Pro</span>
            </Link>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          {/* Search Form */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="font-mono">Rastrear mi equipo</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Código de orden (Ej: TC-2025-ABC123)"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    className="pl-10"
                    data-testid="tracking-search-input"
                  />
                </div>
                <Button type="submit" data-testid="tracking-search-btn">
                  Buscar
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 text-destructive">
                  <AlertCircle className="w-5 h-5" />
                  <p>{error}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Order Details */}
          {order && !loading && (
            <div className="space-y-6 animate-fade-in">
              {/* Status Hero */}
              <Card className="glass-card overflow-hidden">
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4">
                    {(() => {
                      const Icon = STATUS_ICONS[order.status] || Package;
                      return <Icon className="w-10 h-10 text-primary" />;
                    })()}
                  </div>
                  <h2 className="font-mono text-2xl font-bold mb-2" data-testid="order-id">
                    {order.order_id}
                  </h2>
                  <div className={`status-badge status-${order.status} inline-block`} data-testid="order-status">
                    {order.status_label}
                  </div>
                </div>
              </Card>

              {/* Equipment Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-mono">Información del Equipo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Tipo</p>
                      <p className="font-medium">{order.equipment.type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Marca</p>
                      <p className="font-medium">{order.equipment.brand}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Modelo</p>
                      <p className="font-medium">{order.equipment.model}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Fecha de ingreso</p>
                      <p className="font-medium font-mono text-sm">{formatDate(order.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-mono">Historial de la orden</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="timeline">
                    {order.logs.map((log, index) => {
                      const Icon = STATUS_ICONS[log.status] || Package;
                      const isLatest = index === order.logs.length - 1;
                      return (
                        <div key={index} className="timeline-item">
                          <div className={`timeline-dot ${isLatest ? 'timeline-dot-active' : 'timeline-dot-completed'}`}>
                            {isLatest && <div className="absolute inset-0 rounded-full animate-ping bg-primary/50" />}
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{STATUS_LABELS[log.status]}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{log.notes}</p>
                            <p className="text-xs text-muted-foreground mt-1 font-mono">
                              {formatDate(log.timestamp)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
